import { Button } from '@1trading/react/components/ui/button';
import { Card, CardContent } from '@1trading/react/components/ui/card';
import { Checkbox, CheckboxCard } from '@1trading/react/components/ui/checkbox';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { BINARY_ANSWERS } from '@/constants/answers';
import { CONTROLLING_PERSON_TYPES } from '@/constants/institution';
import { InstitutionFieldsetPerson } from '@/features/institution/components/fieldsets/person';
import { useInstitutionDetailsV2Answers } from '@/features/institution/hooks/useInstitutionDetails';
import { createInstitutionPerson } from '@/features/institution/schemas/person';
import {
  CONTROLLING_PERSONS_MAX,
  getInstitutionControllingPersonCandidates,
  type InstitutionTaxSelfCertification,
  type InstitutionTaxSelfCertificationOrDefaults,
  isControllingPersonsApplicable,
} from '@/features/institution/tax-self-certification/schema';
import { reportTaxResidenceFormatErrors } from '@/features/tax-residency/report';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';

const InstitutionTaxSelfCertificationFieldsetControllingPersonsLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.taxSelfCertification.controllingPersons.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.taxSelfCertification.controllingPersons.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionTaxSelfCertificationFieldsetControllingPersonsLegend.displayName = 'InstitutionTaxSelfCertificationFieldsetControllingPersonsLegend';

const InstitutionTaxSelfCertificationFieldsetControllingPersons = () => {
  const form = useFormContext<InstitutionTaxSelfCertificationOrDefaults>();
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'controllingPersons' });
  const { data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { t } = useTranslation();
  const controllingPersons = form.watch('controllingPersons') ?? [];
  const addedPartyIds = new Set(controllingPersons.map(({ partyId }) => partyId));
  const candidatesToCopy = getInstitutionControllingPersonCandidates(institutionDetailsAnswers?.ownership).filter(({ partyId }) => !addedPartyIds.has(partyId));

  // The copy keeps the person's partyId, which marks the same person across sections
  const addControllingPersonFromCandidate = (partyId: string) => {
    const candidate = candidatesToCopy.find((person) => person.partyId === partyId);

    if (candidate) append(candidate, { shouldFocus: false });
  };

  return (
    <FormField
      control={form.control}
      name="controllingPersons"
      render={({ fieldState: { error } }) => (
        <FormItem>
          <div className="space-y-6">
            {fields.map((controllingPerson, index) => (
              <Card key={controllingPerson.id} className="shadow-none" bordered>
                <CardContent className="pt-6">
                  <fieldset className="relative space-y-6">
                    <legend className="font-medium">
                      {t('institutionDetails.taxSelfCertification.controllingPersons.controllingPerson')} {index + 1}
                    </legend>
                    <Button
                      className="absolute top-[-0.66rem] right-[-0.66rem] !m-0 text-destructive hover:text-destructive"
                      size="icon"
                      type="button"
                      variant="ghost"
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="size-4" />
                      <span className="sr-only">
                        {t('institutionDetails.taxSelfCertification.controllingPersons.remove')} {index + 1}
                      </span>
                    </Button>
                    <InstitutionFieldsetPerson<InstitutionTaxSelfCertificationOrDefaults> isContactable={false} name={`controllingPersons.${index}`} />
                    <FormField
                      control={form.control}
                      name={`controllingPersons.${index}.isPep`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('institutionDetails.taxSelfCertification.controllingPersons.isPep')}*</FormLabel>
                          <FormDescription>{t('institutionDetails.person.isPepDescription')}</FormDescription>
                          <FormControl>
                            <RadioGroup
                              className="grid-cols-2"
                              defaultValue={field.value}
                              variant="cards"
                              aria-label={t('institutionDetails.taxSelfCertification.controllingPersons.isPep')}
                              onValueChange={field.onChange}
                            >
                              {BINARY_ANSWERS.map((answer) => (
                                <FormItem key={answer}>
                                  <RadioGroupItemCard variant="outline">
                                    <FormControl>
                                      <RadioGroupItem value={answer} />
                                    </FormControl>
                                    <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                                  </RadioGroupItemCard>
                                </FormItem>
                              ))}
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`controllingPersons.${index}.controllingPersonTypes`}
                      render={() => (
                        <FormItem>
                          <FormLabel>{t('institutionDetails.taxSelfCertification.controllingPersons.controllingPersonTypes')}*</FormLabel>
                          <FormControl aria-label={t('institutionDetails.taxSelfCertification.controllingPersons.controllingPersonTypes')}>
                            {/* biome-ignore lint/a11y/useSemanticElements: Makeshift checkboxgroup since it's not a native role */}
                            <div className="space-y-4" role="group">
                              {CONTROLLING_PERSON_TYPES.map((type) => (
                                <FormField
                                  key={type}
                                  control={form.control}
                                  name={`controllingPersons.${index}.controllingPersonTypes`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <CheckboxCard variant="outline">
                                        <FormControl>
                                          <Checkbox
                                            checked={field.value?.includes(type)}
                                            variant="cards"
                                            onCheckedChange={(checked) => field.onChange(checked ? [...(field.value ?? []), type] : field.value?.filter((value) => value !== type))}
                                          />
                                        </FormControl>
                                        <FormLabel contained>{t(`institutionDetails.taxSelfCertification.controllingPersons.controllingPersonTypeOptions.${type}`)}</FormLabel>
                                      </CheckboxCard>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </fieldset>
                </CardContent>
              </Card>
            ))}
            {fields.length < CONTROLLING_PERSONS_MAX ? (
              <div className="flex flex-wrap gap-2">
                {candidatesToCopy.length ? (
                  <Select value="" onValueChange={addControllingPersonFromCandidate}>
                    <SelectTrigger className="h-9 w-auto gap-2" aria-label={t('institutionDetails.taxSelfCertification.controllingPersons.addFromOwnership')}>
                      <SelectValue placeholder={t('institutionDetails.taxSelfCertification.controllingPersons.addFromOwnership')} />
                    </SelectTrigger>
                    <SelectContent>
                      {candidatesToCopy.map((candidate) => (
                        <SelectItem key={candidate.partyId} value={candidate.partyId}>
                          {candidate.firstName} {candidate.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
                <Button className="gap-2" size="sm" type="button" variant="outline" onClick={() => append({ ...createInstitutionPerson(), controllingPersonTypes: [] })}>
                  <Plus className="size-4" />
                  {t('institutionDetails.taxSelfCertification.controllingPersons.add')}
                </Button>
              </div>
            ) : null}
          </div>
          {error?.root?.message || error?.message ? <FormMessage>{error.root?.message ?? error.message}</FormMessage> : null}
        </FormItem>
      )}
    />
  );
};
InstitutionTaxSelfCertificationFieldsetControllingPersons.displayName = 'InstitutionTaxSelfCertificationFieldsetControllingPersons';

const beforeStepEnter: FormStepGuard<InstitutionTaxSelfCertification> = async ({ direction, form, steps }, next) => {
  if (!form) return next(false);

  if (!isControllingPersonsApplicable(form.getValues())) {
    return next(steps.indexOf(InstitutionTaxSelfCertificationFieldsetControllingPersons) + (direction === 'next' ? 1 : -1));
  }

  return next();
};

InstitutionTaxSelfCertificationFieldsetControllingPersons.beforeStepEnter = beforeStepEnter;

const beforeStepLeave: FormStepGuard<InstitutionTaxSelfCertification> = async ({ direction, form }, next) => {
  if (direction === 'next') await reportTaxResidenceFormatErrors(form.getValues('controllingPersons').flatMap(({ taxResidences }) => taxResidences));

  return next();
};

InstitutionTaxSelfCertificationFieldsetControllingPersons.beforeStepLeave = beforeStepLeave;

export { InstitutionTaxSelfCertificationFieldsetControllingPersons, InstitutionTaxSelfCertificationFieldsetControllingPersonsLegend };
